<template>
    <el-input
        :id="id"
        ref="inputRef"
        v-model="modelInput"
        :maxlength="maxLength"
        :minlength="minLength"
        :readonly="readonly"
        :autocomplete="autocomplete"
        :disabled="disabled"
        :name="name"
        :placeholder="placeholder"
        :type="type"
        :size="size"
        :clearable="clearable"
        :class="{
            'is--error': isError
        }"
        :data-test="dataTestName"
        :data-testid="dataTestName || 'input-text'"
           class="redesign-base-input"
        v-bind="$attrs"
    >
        <template
            v-if="prefixIcon"
            #prefix
        >
            <redesign-base-icon
                :name="prefixIcon"
                :size="prefixIconSize"
                :class="prefixIconClass"
            />
        </template>
        <template
            v-if="suffixIcon"
            #suffix
        >
            <redesign-base-icon
                :name="suffixIcon"
                :size="suffixIconSize"
                :class="prefixIconClass"
            />
        </template>
    </el-input>
</template>

<script setup lang="ts">
import RedesignBaseIcon from '@/components/RedesignBaseComponents/RedesignBaseIcon.vue'
import { computed, ref } from 'vue'
import { TranslateResult } from 'vue-i18n'

type TInputNativeType = 'email' | 'text' | 'password' | 'number' | 'textarea' | 'tel'

interface IIProps {
    id?: string
    disabled?: boolean
    clearable?: boolean
    maxLength?: number
    minLength?: number
    prefixIconSize?: number
    prefixIconClass?: string
    readonly?: boolean
    autocomplete?: string
    placeholder?: string | TranslateResult
    dataTestName?: string
    prefixIcon?: string
    name?: string
    type?: TInputNativeType
    isError?: boolean
    suffixIconSize?: number
    suffixIcon?: string
    suffixIconClass?: string
    size?: 'medium' | 'small' | 'mini'
    value?: string | number
}

const props = withDefaults(
    defineProps<IIProps>(),
    {
        id: undefined,
        name: undefined,
        disabled: false,
        maxLength: undefined,
        minLength: undefined,
        prefixIconSize: 20,
        autocomplete: 'off',
        placeholder: '',
        dataTestName: 'base-input',
        prefixIcon: '',
        suffixIcon: '',
        suffixIconSize: 20,
        type: 'text',
        size: 'small',
        value: undefined,
        suffixIconClass: 'text-placeholders',
        prefixIconClass: 'text-placeholders'
    }
)

const emit = defineEmits<{
    (e: 'input', pl: string | number | undefined): void
    (e: 'update:modelValue', pl: string | number | undefined): void
    (e: 'clear'): void
}>()

const modelInput = computed({
    get () {
        return props.value
    },
    set (val: string | number | undefined) {
        emit('input', val)
        emit('update:modelValue', val)
    }
})

const inputRef = ref()

/* Methods */
// function onClear (): void {
//     modelInput.value = ''
//     emit('clear')
// }
function focus () {
    inputRef.value?.focus()
}
function blur () {
    inputRef.value?.blur()
}

function select () {
    inputRef.value?.select()
}

defineExpose({
    focus,
    blur,
    select
})
</script>

<style lang="scss">
    .redesign-base-input, .el-input.redesign-base-input {
        .el-input__prefix {
            @apply flex items-center justify-center;
        }
        .el-input__suffix {
            .el-input__clear:hover {
                @apply text-active-elements;
            }
        }
        .el-input__inner {
            @apply px-2;
        }
        &.el-input--suffix {
            .el-input__inner {
                @apply ltr:pr-7 rtl:pl-7;
            }
        }
        &.el-input--prefix {
            .el-input__inner {
                @apply ltr:pl-7 rtl:pr-7;
            }
        }
        &.is-disabled {
            @apply opacity-50;
        }
    }

    [dir='rtl'] {
        .redesign-base-input, .el-input.redesign-base-input {
            &.el-input--suffix {
                .el-input__suffix {
                    @apply right-auto left-1;
                }
            }
            &.el-input--prefix {
                .el-input__prefix {
                    @apply left-auto right-1;
                }
            }
        }
    }
</style>
